/**
 * NRC ADAMS Document Library — search for regulatory documents relevant to a site.
 *
 * Searches for Early Site Permits (ESP), Combined License Applications (COLA),
 * Environmental Impact Statements (EIS), and other NRC documents by state/region.
 *
 * Migrated 2026-07-27 from the retired WBA endpoint to the ADAMS Public Search
 * (APS) REST API, per NRC "ADAMS Public Search API — Developer's Guide" v1.0.
 * Three things changed, not just the hostname:
 *   - Host:      adams.nrc.gov (NXDOMAIN) -> adams-api.nrc.gov
 *   - Transport: GET + URL-encoded WBA query DSL -> POST + JSON body
 *   - Auth:      none -> Ocp-Apim-Subscription-Key header (so it is NO LONGER
 *                keyless; the key is server-side only, via /api/adams/search)
 *
 * Docs/portal: https://adams-api-developer.nrc.gov/
 * Public site: https://adams-search.nrc.gov/
 */
import { CACHE_TTL } from './types';
import { authedFetch } from '../../lib/authedFetch';
import type { DataProvider, ProviderGeoContext, ProviderResult } from './types';
import type { DataSource } from '../../types/siteRag';
import { rethrowIfAborted, throwIfAborted } from '../../lib/abortSignal';

export interface AdamsDocument {
  accessionNumber: string;
  title: string;
  date: string;
  documentType: string;
  docketNumber?: string;
  url?: string;
}

export interface NrcAdamsData {
  documents: AdamsDocument[];
  totalResults: number;
  searchQuery: string;
}

/**
 * One `results[]` entry from the APS search response.
 *
 * Shape per NRC "ADAMS Public Search API — Developer's Guide" v1.0, Appendix C.
 * Several properties are ARRAYS (`AuthorName`, `DocumentType`, `DocketNumber`)
 * where the retired XML feed carried scalars — flattening them is the main
 * translation hazard in this file.
 */
interface ApsSearchResult {
  document?: {
    AccessionNumber?: string;
    DocumentTitle?: string;
    Name?: string;
    DocumentDate?: string;
    DocumentType?: string[] | string;
    DocketNumber?: string[] | string;
    Url?: string;
  };
}

/** Take the first meaningful entry of an APS field that may be scalar or array. */
function firstOf(value: string[] | string | undefined): string {
  if (Array.isArray(value)) return value.find(v => typeof v === 'string' && v.trim() !== '')?.trim() ?? '';
  return typeof value === 'string' ? value.trim() : '';
}

/** Join an APS array field for display, preserving every value. */
function joinOf(value: string[] | string | undefined): string {
  if (Array.isArray(value)) return value.filter(v => typeof v === 'string' && v.trim() !== '').join(', ');
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Parse the APS JSON search response into the provider's document array.
 *
 * Prefers the API's own `Url` property for the document link. The guide's
 * sample returns a resolvable `https://www.nrc.gov/docs/...` URI, which is the
 * only citation target the API itself vouches for — this file does NOT
 * reconstruct one from the accession number, because a hand-built link that
 * looks authoritative and 404s is worse than no link at all.
 */
function parseApsResults(payload: unknown): AdamsDocument[] {
  const results = (payload as { results?: unknown })?.results;
  if (!Array.isArray(results)) return [];
  const docs: AdamsDocument[] = [];
  for (const entry of results as ApsSearchResult[]) {
    const d = entry?.document;
    const accession = typeof d?.AccessionNumber === 'string' ? d.AccessionNumber.trim() : '';
    if (!accession) continue; // skip malformed entries
    docs.push({
      accessionNumber: accession,
      title: (d?.DocumentTitle ?? d?.Name ?? '').trim(),
      date: (d?.DocumentDate ?? '').trim(),
      documentType: joinOf(d?.DocumentType),
      docketNumber: firstOf(d?.DocketNumber) || undefined,
      url: typeof d?.Url === 'string' && d.Url.trim() !== '' ? d.Url.trim() : undefined,
    });
  }
  return docs;
}

/**
 * Server route that attaches the APS subscription key.
 *
 * The credential is an `Ocp-Apim-Subscription-Key` HEADER, so it cannot ride
 * the general `/api/proxy` (GET-only, query-param key injection) and must
 * never reach the browser. The client sends the query shape; the server
 * attaches the key.
 */
const ADAMS_APS_ROUTE = '/api/adams/search';

/**
 * POST the APS query to our own route.
 *
 * Deliberately not `fetchWithTimeout`: that helper rewrites any URL into
 * `/api/proxy?url=…` and offers no request init, so it can neither POST a body
 * nor address a same-origin route. `authedFetch` still supplies the Firebase
 * bearer token the route requires.
 */
async function postApsSearch(
  body: Record<string, unknown>,
  timeoutMs: number,
  signal?: AbortSignal,
): Promise<Response> {
  const controller = new AbortController();
  if (signal?.aborted) controller.abort(signal.reason);
  const onAbort = () => controller.abort(signal?.reason);
  signal?.addEventListener('abort', onAbort, { once: true });
  const timer = setTimeout(
    () => controller.abort(new Error(`ADAMS request timed out after ${timeoutMs}ms`)),
    timeoutMs,
  );
  return authedFetch(ADAMS_APS_ROUTE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal: controller.signal,
  }).finally(() => {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onAbort);
  });
}

/**
 * Build the APS search request body.
 *
 * Per the Developer's Guide §3.1.2: `q` is the free-text query, `filters` is
 * AND, `anyFilters` is OR, and the two library flags select Main (post-1999)
 * and Legacy (pre-1999). Both libraries are enabled — a siting review wants the
 * historical record, and Legacy is only reachable through this API.
 *
 * This replaces the retired WBA query DSL
 * (`(mode:sections,sections:(filters:(public-library:!t),…))`), which was a
 * URL-encoded expression against a host that no longer resolves.
 */
function buildApsSearchBody(searchTerms: string): Record<string, unknown> {
  return {
    q: searchTerms,
    filters: [],
    anyFilters: [],
    mainLibFilter: true,
    legacyLibFilter: true,
    sort: 'DocumentDate',
    sortDirection: 1, // 1 = descending — newest first.
    skip: 0,
  };
}

/**
 * Fail-fast breaker for an unreachable ADAMS host.
 *
 * The endpoint is now correct (see the file header), but the source can still
 * be unreachable — or unconfigured, if this deployment has no APS key. The
 * public-sentiment orchestrator calls this provider once per tech × locality,
 * so without a breaker one site refresh fired dozens of doomed requests and
 * spent minutes failing. That is how the dead-host outage presented before the
 * migration. One failure is enough to know the source is down for this run:
 * trip the breaker and skip the rest.
 *
 * Deliberately time-boxed rather than permanent. A 5xx can be transient, and a
 * process-lifetime latch would keep a recovered source dark until redeploy, so
 * the breaker re-arms after the cooldown and the next refresh probes again.
 */
const ADAMS_UNREACHABLE_COOLDOWN_MS = 60_000;
let adamsUnreachableUntilMs = 0;

/** Test seam — the breaker is module state and would leak across cases. */
export function __resetAdamsUnreachableForTest(): void {
  adamsUnreachableUntilMs = 0;
}

function adamsCircuitOpen(): boolean {
  return Date.now() < adamsUnreachableUntilMs;
}

/**
 * A 5xx from OUR proxy means it could not reach the upstream at all (DNS
 * failure, refused connection, gateway timeout) — distinct from ADAMS itself
 * answering with an error, which would arrive as a 4xx and is not a reason to
 * stop querying.
 */
function isUnreachableStatus(status: number): boolean {
  return status === 502 || status === 503 || status === 504;
}

/** Priority document types for site feasibility assessment. */
const PRIORITY_KEYWORDS = [
  'early site permit',
  'combined license',
  'environmental impact',
  'safety evaluation',
  'site characterization',
  'construction permit',
  'COLA',
  'ESP',
  'EIS',
];

/**
 * Score a document's relevance to nuclear site feasibility.
 * Higher score = more relevant.
 */
function relevanceScore(doc: AdamsDocument, state: string | null): number {
  let score = 0;
  const titleLower = doc.title.toLowerCase();
  const typeLower = doc.documentType.toLowerCase();

  for (const kw of PRIORITY_KEYWORDS) {
    if (titleLower.includes(kw.toLowerCase()) || typeLower.includes(kw.toLowerCase())) {
      score += 10;
    }
  }

  // Boost documents mentioning the target state
  if (state && titleLower.includes(state.toLowerCase())) {
    score += 20;
  }

  // Boost newer documents
  const year = parseInt(doc.date.slice(-4), 10);
  if (year >= 2020) score += 5;
  else if (year >= 2015) score += 3;

  return score;
}

/**
 * Search ADAMS for NRC documents relevant to a site's state and nuclear context.
 * Runs two targeted queries and merges/dedupes results for best coverage.
 */
export async function searchAdams(
  state: string | null,
  searchContext: string = 'nuclear site',
  maxResults: number = 10,
  signal?: AbortSignal,
): Promise<{ data: NrcAdamsData; sources: DataSource[] }> {
  throwIfAborted(signal);
  const allDocs: AdamsDocument[] = [];
  const queries: string[] = [];
  /** Set when the host could not be reached — NOT the same as "found nothing". */
  let unreachable = false;
  /** Set when the deployment has no (or an invalid) APS subscription key. */
  let unconfigured = false;

  // Query 1: ESP/COLA/EIS + state name
  const sitingTerms = state
    ? `"early site permit" OR "combined license" OR "environmental impact statement" ${state}`
    : '"early site permit" OR "combined license application"';
  queries.push(sitingTerms);

  // Query 2: Site-specific context (e.g., SMR + state, or custom context)
  const contextTerms = state
    ? `${searchContext} ${state}`
    : searchContext;
  if (contextTerms !== sitingTerms) {
    queries.push(contextTerms);
  }

  // Breaker already tripped by an earlier call in this fan-out — skip the
  // network entirely rather than re-proving the host is down.
  if (adamsCircuitOpen()) {
    unreachable = true;
  }

  for (const terms of queries) {
    if (unreachable) break;
    throwIfAborted(signal);
    try {
      const res = await postApsSearch(buildApsSearchBody(terms), 45_000, signal);
      throwIfAborted(signal);
      if (!res.ok) {
        // 503 = no subscription key on this deployment; 401/403 = the key is
        // bad. Neither is "we searched and found nothing", and neither is
        // fixed by asking again, so stop and report.
        if (res.status === 503 || res.status === 401 || res.status === 403) {
          unconfigured = true;
          unreachable = true;
          adamsUnreachableUntilMs = Date.now() + ADAMS_UNREACHABLE_COOLDOWN_MS;
          break;
        }
        if (isUnreachableStatus(res.status)) {
          // The host is unreachable, not merely unhelpful. Stop here — the
          // remaining queries would fail identically.
          unreachable = true;
          adamsUnreachableUntilMs = Date.now() + ADAMS_UNREACHABLE_COOLDOWN_MS;
          break;
        }
        continue;
      }
      const payload = await res.json() as { count?: number };
      throwIfAborted(signal);

      // Zero hits is a real answer from a live source — not a failure.
      if (!payload || payload.count === 0) continue;

      const docs = parseApsResults(payload);
      allDocs.push(...docs);
    } catch (error) {
      rethrowIfAborted(signal, error);
      // A throw here is a transport failure (DNS, refused, timeout) rather
      // than a bad answer — same conclusion as a 5xx, so trip and stop.
      unreachable = true;
      adamsUnreachableUntilMs = Date.now() + ADAMS_UNREACHABLE_COOLDOWN_MS;
      break;
    }
  }

  throwIfAborted(signal);
  // Dedupe by accession number
  const seen = new Set<string>();
  const unique = allDocs.filter(d => {
    if (seen.has(d.accessionNumber)) return false;
    seen.add(d.accessionNumber);
    return true;
  });

  // Sort by relevance score (descending), then by date (descending)
  const scored = unique
    .map(d => ({ doc: d, score: relevanceScore(d, state) }))
    .sort((a, b) => b.score - a.score || b.doc.date.localeCompare(a.doc.date))
    .slice(0, maxResults)
    .map(s => s.doc);

  const queryDesc = queries.join(' | ');

  return {
    data: {
      documents: scored,
      totalResults: unique.length,
      searchQuery: queryDesc,
    },
    sources: [{
      field: 'nrc_documents',
      // "Unreachable" and "found nothing" are different claims and must not
      // collapse into one string. Reporting a dead host as "no documents found"
      // told every downstream reader that ADAMS had been searched and the site
      // genuinely has no NRC record — an assertion made on zero evidence, and
      // indistinguishable from a real negative result.
      source: unconfigured
        ? `NRC ADAMS not configured — this deployment has no ADAMS_APS_API_KEY, so the library was NOT searched. Register at https://adams-api-developer.nrc.gov/ and set the key. This is not a finding of "no documents".`
        : unreachable
          ? `NRC ADAMS unavailable — the source could not be reached, so it was NOT searched. This is not a finding of "no documents"; the section stays uncollected until the source responds.`
          : scored.length > 0
          ? `NRC ADAMS — ${unique.length} documents found. Top ${scored.length} by relevance: ${scored.slice(0, 3).map(d => d.accessionNumber).join(', ')}`
          : `NRC ADAMS — no documents found for "${queryDesc}"`,
      url: 'https://www.nrc.gov/reading-rm/adams.html',
      retrievedAt: new Date().toISOString(),
    }],
  };
}

/**
 * Synthetic provider — wraps `searchAdams` so the SED top-up
 * dispatcher can refresh the `adamsDocuments` section like any other
 * provider (Content-Source Fixes Prompt 5). Uses a generic nuclear-
 * siting query when no state context is available; the orchestrator
 * passes lat/lng only via this signature, so we resolve state from
 * coords inline (cheap census lookup).
 *
 * Defensive: a failed ADAMS query returns an empty document set —
 * never throws. The orchestrator's try/catch flow records the section
 * as `unavailable` when no docs come back.
 */
export const nrcAdamsProvider: DataProvider<NrcAdamsData> = {
  id: 'nrc-adams',
  name: 'NRC ADAMS Document Index',
  cacheTtlMs: CACHE_TTL.STANDARD,
  requiresKey: false,

  async fetch(
    lat: number,
    lng: number,
    _apiKey?: string,
    geo?: ProviderGeoContext,
  ): Promise<ProviderResult<NrcAdamsData>> {
    // CA3: `(lat, lng)` is the boundary centroid when a polygon is drawn
    // (`nrc-adams` is in CENTROID_QUERY_PROVIDER_IDS), else the geocoded
    // address. The curated ADAMS query is keyed off the state resolved from
    // the query point — a mailbox is essentially always in the same state as
    // its parcel, so a coordinate-consistency change; near a state line it's
    // the parcel that matters.
    let state: string | null = null;
    try {
      const { resolveStateFromCoords } = await import('./regulatoryDb');
      state = await resolveStateFromCoords(lat, lng, geo?.signal);
    } catch {
      // Coords outside US coverage or geo lookup unreachable — fall
      // through to a stateless query.
    }
    try {
      return await searchAdams(state, 'nuclear site characterization', 10, geo?.signal);
    } catch (e) {
      // ADAMS upstream sometimes 5xxs under load. Don't propagate;
      // surface as an empty result so the orchestrator can mark the
      // section unavailable instead of degrading the whole report.
      return {
        data: { documents: [], totalResults: 0, searchQuery: '' },
        sources: [{
          field: 'adamsDocuments',
          source: `NRC ADAMS unavailable: ${(e as Error)?.message ?? 'unknown error'}`,
          retrievedAt: new Date().toISOString(),
        }],
      };
    }
  },
};
